<template>
    <div>
        <div class="revenue__page">
            <div>
                <div class="revenue__stats__wrap">
                    <div class="mt--10">
                        <p class="text--semi--bold text--lg text-gray-700">
                            Revenue Breakdown
                        </p>
                        <p class="text--normal text--sm text-gray-600 mt--5">
                            Understand how revenue gotten from paid projects and paid invoices typically progress. Below is an overview of your revenue performance, including total revenue, average revenue and income breakdown by clients.
                        </p>
                    </div>

                    <div class="flex align-items-center justify-content-end mt--20">
                        <div class="dropdown mr--5" :class="{ 'disabled': statsLoading }">
                            <BaseButton
                                data-bs-toggle="dropdown" 
                                aria-expanded="false"
                                is-content
                                variant="outline" 
                                variant-style="secondary"
                                class="dropdown-toggle" 
                            >
                                <template #content>
                                    <span class="mr--5 text--upper text--semi--bold">Year:</span>
                                    <span>{{ String(filter.year) }}</span>
                                </template>
                            </BaseButton>

                            <ul class="dropdown-menu dropdown--menu">
                                <li 
                                    v-for="year in ['2024', '2025']" 
                                    :key="year" 
                                    class="pointer dropdown-item dropdown--item"
                                    :class="{ 'selected': filter.year === year }" 
                                    @click="setFilter({ year })"
                                >
                                    <span class="text--xs">{{ year }}</span>
                                </li>
                            </ul>
                        </div>

                        <div :class="{ 'disabled': statsLoading }">
                            <div class="dropdown">
                                <BaseButton
                                    data-bs-toggle="dropdown" 
                                    aria-expanded="false" 
                                    is-suffix-icon
                                    is-content
                                    variant="outline" 
                                    variant-style="secondary"
                                    class="dropdown-toggle text--upper" 
                                    icon-name="chevron-down" 
                                    icon-width="12px" 
                                >
                                    <template #content>
                                        <span class="mr--5 text--upper text--semi--bold">Currency:</span>
                                        <span>{{ filter.currency }}</span>
                                    </template>
                                </BaseButton>
                                <ul class="dropdown-menu dropdown--menu currency">
                                    <li 
                                        v-for="item in settlementAccountCurrencies" 
                                        :key="item" 
                                        class="dropdown-item pointer dropdown--item"
                                        :class="{ 'selected': filter.currency === item }" 
                                        @click="setFilter({ currency: item })"
                                    >
                                        <span class="text--xs text--upper">{{ item }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div class="revenue__stats__list mt--20">
                        <template v-if="statsLoading">
                            <div class="revenue__stats__list">
                                <StatsSkeleton v-for="item in 6" :key="item" />
                            </div>
                        </template>
    
                        <template v-else-if="fetchStatsErr">
                            <EmptyPage state="error" type="revenue" @action="handleFetchStats" />
                        </template>   

                        <template v-else>
                            <div class="revenue__stats__list">
                                <div class="revenue__stats__list__item">
                                    <Stats
                                        :title="`Revenue in ${currencySymbol?.symbol} for current month`"
                                        :value="transformedStats.totalRevenueCurrentMonth"
                                        :currency="filter.currency"
                                        show-tool-tip
                                        :tool-tip-text="currentMonthDesc"
                                    />
                                </div>

                                <div class="revenue__stats__list__item">
                                    <Stats
                                        :title="`Average Revenue in ${currencySymbol?.symbol} Per Month`"
                                        :value="transformedStats.totalRevenuePerMonth"
                                        :currency="filter.currency"
                                        show-tool-tip
                                        tool-tip-text="How much you make per month on the average"
                                    />
                                </div>

                                <div class="revenue__stats__list__item">
                                    <Stats
                                        :title="`Average Revenue in ${currencySymbol?.symbol} for Current Quarter`"
                                        :value="transformedStats.currentQuarterRevenue"
                                        :percentage="getComparison(transformedStats.lastQuarterRevenue, transformedStats.currentQuarterRevenue)"
                                        show-tool-tip
                                        tool-tip-text="Your earnings split by quarters"
                                    />
                                </div>

                                <div class="revenue__stats__list__item">
                                    <Stats
                                        :title="`Total Revenue in ${currencySymbol?.symbol}`"
                                        :value="transformedStats.currentYearRevenue"
                                        :percentage="getComparison(transformedStats.lastYearRevenue, transformedStats.currentYearRevenue)"
                                        :currency="filter.currency"
                                        show-tool-tip
                                        :tool-tip-text="DATE_FILTERS_MAP[$route?.query?.range]"
                                    />
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { EmptyPage, BaseButton } from "@/components/ui/index";
import Stats from "@/components/stats/Stats.vue";
import StatsSkeleton from "@/components/stats/StatsSkeleton.vue";
import { settlementAccountCurrencies, supportedCurrenciesMap } from "@/plugins/countries";
import { getMonthFromNum, serilaizeQuery } from "@/utils/helpers";
import { DATE_FILTERS_MAP } from "@/utils/dummy";

export default {
    name: "RevenueStats",

    components: {
        EmptyPage,
        BaseButton,
        Stats,
        StatsSkeleton,
    },

    data() {
        return {
            settlementAccountCurrencies,
            DATE_FILTERS_MAP,
            statsLoading: false,
            fetchStatsErr: false,
            fetchStatsErrMsg: "",
            form: {
                name: "",
                isCompleted: false,
            },
            formActionType: "add",
            deleteLoading: false,
            previousQuarterAmt: 0,
            filter: {
                year:  this.$route.query.year || new Date().getFullYear(),
                currency: this.$route.query.currency || "ngn",
            },
        };
    },

    computed: {
        stats() {
            return this.$store.getters["revenue/stats"];
        },

        earnings() {
            return this.$store.getters["revenue/earnings"];
        },

        transformedStats() {
            const statsData = {
                lastQuarterRevenue: 0,
                yearlyTotalRevenue: 0,
            };

            if (!Array.isArray(this.stats) || !this.stats.length) return statsData;
            
            const statsObj = Object.entries(this.stats[0]);
            
            statsObj.forEach(([key, values]) => {
                if (key && !values.length) {
                    return statsData[key] = 0;
                }                

                switch (key) {
                case "totalRevenueCurrentMonth":
                    statsData[key] = values[0]?.totalAmount || 0;
                    break;
                    
                case "totalRevenuePerMonth":
                    statsData[key] = values.find((v) => v._id === "averageMonthly")?.totalAmount || 0;;
                    break;
                        
                case "totalRevenueQuartely":
                    const lastQuarterRevenue = values.find((v) => v._id === "previousQuarter")?.totalAmount || 0;
                    const currentQuarterRevenue = values.find((v) => v._id === "currentQuarter")?.totalAmount || 0;
                    // const yearlyTotalRevenue =  statsData?.averageMonthlyRevenue * 3 || 0; // Assuming average quarter = 3 months

                    statsData.lastQuarterRevenue = lastQuarterRevenue;
                    statsData.currentQuarterRevenue = currentQuarterRevenue;
                    break;
                         
                case "totalRevenueYear":                    
                    const lastYearRevenue = values.find((v) => v._id === "previousYear")?.totalAmount || 0;
                    const currentYearRevenue = values.find((v) => v._id === "currentYear")?.totalAmount || 0;                    
                        
                    statsData.lastYearRevenue = lastYearRevenue;
                    statsData.currentYearRevenue = currentYearRevenue;
                    break;
                         
                case "totalRevenuePerClient":
                    statsData[key] = values[0]?.totalAmount || 0;
                    break;

                case "totalRevenuePerProject":
                    statsData[key] = values[0]?.totalAmount || 0;
                    break;
                
                default:
                    break;
                }

            });

            return statsData;
        },

        currentMonthDesc() {
            const year = this.filter.year;
            const currentMonth = new Date().getMonth();

            const month = getMonthFromNum(currentMonth, false);

            return `${month} ${year}`;
        },

        currencySymbol() {      
            if (!this.filter.currency) return "$";

            const lowerCaseCurrency = this.filter.currency?.toLowerCase();            
            return supportedCurrenciesMap[lowerCaseCurrency] || "$";      
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {                
                await this.handleFetchStats({ refresh: true, ...newVal });
            },
            deep: true,
            immediate: true,
        },
    },

    methods: {  
        getComparison(previous, current) {
            if (previous === 0 || current === 0) return 0;

            console.log({ previous, current });
            
            
            const difference = current - previous;
            const percentage = (((difference) / previous) * 100).toFixed(2);
            
            return percentage;
        },

        setFilter(params) {
            this.filter = {
                ...this.filter,
                ...params,
            };

            const currentRoute = this.$route.path;
            const queryParam = serilaizeQuery(this.filter);

            this.$router.replace(`${currentRoute}?${queryParam}`);
        },

        async handleFetchStats(params) {
            try {
                this.fetchStatsErr = false;
                this.statsLoading = true;

                const payload = {
                    currency: params?.currency?.toLowerCase() || this.filter.currency,
                    year: params?.year || this.filter.year,
                };
                
                await this.$store.dispatch("revenue/fetchStats", payload);
            } catch (error) {
                console.log({ handleFetchStats: error });
                this.fetchStatsErr = true;
            } finally {
                this.statsLoading = false;
            }
        },
    },
};
</script>

<style lang="scss">
    .earning-icon-link {
        margin-left: 3px;
    }
</style>